import { useEffect, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import i18n from 'commons/libs/i18n'
import { formatDate } from 'commons/utils/date'
import { Breadcrumb, InternationalSampleHeader, Page, ToolBar } from 'components'
import { Channels, Organization, OVTypes, TypesValidations } from 'constants/enums'
import type { Cliente } from 'container/CreateOrder'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { postMaintainDraft } from 'services/draft'
import { resetSalesOrder } from 'store/SalesOrderContext/salesOrderSlice'
import { useDispatch } from 'store/store'

import { MaterialInsertCard } from '../components/MaterialInsertCard'
import * as S from './styles'
import { formSchema } from './validations'

export const ModelSpreadsheetUrl = `${process.env.REACT_APP_DOWNLOAD_FILES_BASE_URL}/modelo_novo_upload_de_planilhas.xlsx`

export const InternationalSampleOrder = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)

  const defaultValues = {
    address: '',
    cd_canl_dstb: Channels.OP,
    cd_cent_cust: '',
    cd_clie: {} as Pick<Cliente, 'cd_clie' | 'cd_empr' | 'cd_equi_vend' | 'cd_regi_vend'>,
    cd_clie_entr: '',
    cd_cond_pgto: '',
    cd_elem_pep: '',
    cd_escr_vend: '',
    cd_inco: '',
    cd_mes_entr: '',
    cd_mes_fatm: '',
    cd_mes_pedi: '',
    cd_moti_prom: '',
    cd_orgn_vend: Organization.ALHV,
    cd_seto_atvd: '',
    cd_tipo_orde: OVTypes.YASC,
    ds_armz: '',
    ds_cond_pgto: '',
    ds_text_nota_fisc: '',
    dt_dsjd_pedi: '',
    fl_cbtr_camb: TypesValidations.NO, // TODO: Keep 'NO' as default until YASC next steps in 2026. Default = ''
    fl_matr_fora_linh: TypesValidations.NO,
    nr_pedi_clie: '',
  }

  const methods = useForm({
    defaultValues,
    mode: 'all',
    resolver: yupResolver(formSchema),
    reValidateMode: 'onBlur',
  })

  const {
    watch,
    reset,
    getValues,
    formState: { isValid },
  } = methods

  useEffect(() => {
    dispatch(resetSalesOrder())
  }, [])

  const customer = watch('cd_clie')
  const cd_escr_vend = watch('cd_escr_vend')
  const cd_seto_atvd = watch('cd_seto_atvd')
  const nr_pedi_clie = watch('nr_pedi_clie')
  const cd_clie_entr = watch('cd_clie_entr')
  const address = watch('address')
  const fl_matr_fora_linh = watch('fl_matr_fora_linh')
  const cd_cond_pgto = watch('cd_cond_pgto')
  const ds_text_nota_fisc = watch('ds_text_nota_fisc')
  const cd_moti_prom = watch('cd_moti_prom')
  const cd_elem_pep = watch('cd_elem_pep')
  const cd_cent_cust = watch('cd_cent_cust')
  const dt_dsjd_pedi = watch('dt_dsjd_pedi')
  const cd_mes_entr = watch('cd_mes_entr')
  const cd_inco = watch('cd_inco')
  const cd_mes_fatm = watch('cd_mes_fatm')
  const ds_armz = watch('ds_armz')
  const fl_cbtr_camb = watch('fl_cbtr_camb')

  const requestBody = {
    address,
    cd_canl_dstb: Channels.OP,
    cd_cent_cust,
    cd_clie: customer.cd_clie,
    cd_clie_entr: address || cd_clie_entr,
    cd_cond_pgto,
    cd_elem_pep,
    cd_empr: customer.cd_empr,
    cd_equi_vend: customer.cd_equi_vend,
    cd_escr_vend,
    cd_inco,
    cd_mes_entr: formatDate(cd_mes_entr, 'YYYY-MM'),
    cd_mes_fatm: formatDate(cd_mes_fatm, 'YYYY-MM'),
    cd_mes_pedi: formatDate(new Date(), 'YYYY-MM'),
    cd_moti_prom,
    cd_orgn_vend: Organization.ALHV,
    cd_regi_vend: customer.cd_regi_vend,
    cd_seto_atvd,
    cd_tipo_orde: OVTypes.YASC,
    ds_armz,
    ds_text_nota_fisc,
    dt_dsjd_pedi: formatDate(dt_dsjd_pedi, 'YYYY-MM-DD'),
    fl_cbtr_camb,
    fl_matr_fora_linh,
    nr_pedi_clie,
  }

  const postDraft = async () => {
    try {
      setLoading(true)
      const response = await postMaintainDraft(requestBody)
      const orderNumberDraft = response?.nr_pedi_rasc?.toString().trim()

      if (orderNumberDraft) {
        toast.success(i18n.t('common.messages.orderSavedSuccessfully'))
        reset(getValues())

        navigate(`/pedidos/${orderNumberDraft}`)
      }
    } catch (error) {
      /* Error handled by interceptor */
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isValid) {
      postDraft()
    }
  }, [
    isValid,
    customer,
    cd_seto_atvd,
    nr_pedi_clie,
    cd_clie_entr,
    address,
    fl_matr_fora_linh,
    cd_cond_pgto,
    ds_text_nota_fisc,
    cd_moti_prom,
    dt_dsjd_pedi,
    cd_mes_entr,
    cd_inco,
    cd_mes_fatm,
    ds_armz,
    fl_cbtr_camb,
  ])

  return (
    <FormProvider {...methods}>
      <Page
        fullwidth
        loading={loading}
        renderHeaderComponent={<ToolBar fullWidth orderStatus="initial" />}
      >
        <Breadcrumb />
        <S.Content>
          <S.Wrapper>
            <InternationalSampleHeader />
            <MaterialInsertCard />
          </S.Wrapper>
        </S.Content>
      </Page>
    </FormProvider>
  )
}

export default InternationalSampleOrder
